/* klaasje-apparel
   ---------------
   Alles wat <klaasje-product-card> doet. Hetzelfde element draagt de
   kaarten in het apparel-raster en de productpagina, dus niet elk stuk
   hieronder draait overal: elke init stapt eruit als de haakjes er niet
   zijn.

   - de fotocarrousel met pijlen en swipe         (raster en PDP)
   - de klik op de foto naar de productpagina      (raster)
   - maten aanvinken, dan het mandje ernaast      (raster)
   - maat, prijs en voorraad achter een keuzelijst (PDP)
   - binnenkomen in beeld                          (raster)

   Toevoegen aan de winkelmand doet Dawn's <product-form>. In het raster
   is zijn submit-knop het winkelmandje naast de maten; de matenknoppen
   zetten alleen het variant-id in het formulier.

   Alles staat in een IIFE. Thema-scripts delen één globale scope en een
   dubbele top-level const sloopt het hele bestand zonder foutmelding in
   beeld (zie CLAUDE.md).
*/

(function () {
  const EXACT_TOT = 3; // tot en met dit aantal noemen we het exacte getal
  const SWIPE = 40; // pixels voor je van foto wisselt

  class KlaasjeProductCard extends HTMLElement {
    connectedCallback() {
      this.initCarousel();
      this.initVariants();
      this.initSizes();
      this.initLink();
      this.initReveal();
    }

    disconnectedCallback() {
      if (this.loadWatcher) this.loadWatcher.disconnect();
    }

    /* ── Foto's ──────────────────────────────────────────────── */
    initCarousel() {
      this.slidesEl = this.querySelector('[data-klaasje-slides]');
      if (!this.slidesEl) return;

      this.slides = this.slidesEl.children;
      this.dots = Array.from(this.querySelectorAll('.klaasje-card__dot'));
      this.index = 0;

      const prev = this.querySelector('[data-klaasje-prev]');
      const next = this.querySelector('[data-klaasje-next]');
      // stopPropagation: bladeren is geen klik op de kaart en opent dus niets
      if (prev) {
        prev.addEventListener('click', (e) => {
          e.stopPropagation();
          this.go(this.index - 1);
        });
      }
      if (next) {
        next.addEventListener('click', (e) => {
          e.stopPropagation();
          this.go(this.index + 1);
        });
      }

      /* geen autoplay: elke kaart staat op zijn eerste foto (eigenaar) */
      this.initSwipe();
    }

    go(i) {
      const total = this.slides.length;
      // rondlopen: voorbij de laatste begin je weer vooraan
      this.index = (i + total) % total;
      this.slidesEl.style.transform = `translateX(-${this.index * 100}%)`;
      this.dots.forEach((dot, d) => dot.classList.toggle('is-active', d === this.index));
    }

    /* Vegen op mobiel. Pas als duidelijk is dat de beweging horizontaal is
       nemen we hem over, anders blijft verticaal scrollen gewoon werken. */
    initSwipe() {
      let sx = 0;
      let sy = 0;
      let dx = 0;
      let dragging = false;
      let decided = false;

      this.slidesEl.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          sx = e.touches[0].clientX;
          sy = e.touches[0].clientY;
          dx = 0;
          dragging = false;
          decided = false;
        },
        { passive: true }
      );

      this.slidesEl.addEventListener(
        'touchmove',
        (e) => {
          if (e.touches.length !== 1) return;
          const mx = e.touches[0].clientX - sx;
          const my = e.touches[0].clientY - sy;
          if (!decided) {
            if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
            decided = true;
            dragging = Math.abs(mx) > Math.abs(my);
          }
          if (!dragging) return;
          e.preventDefault();
          dx = mx;
          this.slidesEl.style.transition = 'none';
          this.slidesEl.style.transform = `translateX(calc(${-this.index * 100}% + ${dx}px))`;
        },
        { passive: false }
      );

      this.slidesEl.addEventListener('touchend', () => {
        if (!dragging) return;
        this.slidesEl.style.transition = '';
        if (Math.abs(dx) > SWIPE) this.go(this.index + (dx < 0 ? 1 : -1));
        else this.go(this.index);
        dragging = false;
        // na een veeg blijft de kaart staan waar de bezoeker hem zette
      });
    }

    /* ── Klik op de foto naar de productpagina ───────────────── */
    initLink() {
      const opener = this.querySelector('[data-klaasje-open]');
      if (!opener || !this.dataset.url) return;

      opener.addEventListener('click', (e) => {
        // de pijlen en de matenlaag doen hun eigen ding
        if (e.target.closest('.klaasje-card__slide-btn, .klaasje-card__buy')) return;
        e.stopPropagation();
        window.location.href = this.dataset.url;
      });
    }

    /* ── Maten over de foto ──────────────────────────────────── */
    /* Twee stappen. Maten kiezen vult het formulier en maakt het
       winkelmandje wakker; dat mandje is Dawn's eigen submit-knop en legt
       ze er pas in. Tegenhouden hoeven we niets: zolang er geen maat staat
       draagt die knop aria-disabled, en daar stapt Dawn's onSubmitHandler
       zelf op uit.

       Kiezen is een schakelaar: nog een keer op dezelfde maat haalt hem
       weer weg, en er mogen er meerdere tegelijk aan staan. Iemand die een
       longsleeve in M en in L wil hoeft dus niet twee keer door de kaart
       heen.

       Een product zonder maten heeft niets te kiezen. Daar is er geen
       mandje en dient die ene knop meteen in via de verborgen submit-knop,
       precies zoals het hiervoor overal werkte. */
    initSizes() {
      this.sizeBtns = Array.from(this.querySelectorAll('[data-klaasje-size]'));
      if (!this.sizeBtns.length) return;

      this.buyForm = this.querySelector('.klaasje-card__buy-form');
      this.cartBtn = this.querySelector('.klaasje-card__cart');
      this.submitBtn = this.cartBtn || this.querySelector('.klaasje-card__submit');
      const idInput = this.querySelector('[data-klaasje-card-id]');
      if (!this.buyForm || !this.submitBtn || !idInput) return;

      this.sizeBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          // niet doorklikken naar de productpagina
          e.preventDefault();
          e.stopPropagation();
          if (btn.disabled) return;

          if (this.cartBtn) {
            const on = !btn.classList.contains('is-selected');
            btn.classList.toggle('is-selected', on);
            btn.setAttribute('aria-pressed', String(on));
            this.syncSizes(idInput);
            return;
          }

          idInput.value = btn.dataset.variantId;
          idInput.disabled = false;
          btn.classList.add('is-adding');
          if (this.buyForm.requestSubmit) this.buyForm.requestSubmit(this.submitBtn);
          else this.submitBtn.click();
        });
      });

      // Met een zichtbaar mandje draagt die knop de laadstand zelf: Dawn zet
      // er 'loading' op en klapt de spinner open. Alleen bij de verborgen
      // knop ziet niemand dat, en spiegelen we het terug naar de maat waarop
      // geklikt is, zodat we zelf niets hoeven te timen.
      if (this.cartBtn) return;

      this.loadWatcher = new MutationObserver(() => {
        if (this.submitBtn.classList.contains('loading')) return;
        this.sizeBtns.forEach((b) => b.classList.remove('is-adding'));
      });
      this.loadWatcher.observe(this.submitBtn, { attributes: true, attributeFilter: ['class'] });
    }

    /* Het formulier achter de gekozen maten aan schrijven.

       Eén maat gaat door Dawn's eigen `id`-veld, precies zoals altijd; dan
       weet <product-form> ook nog welke variant het was en klopt zijn
       melding naar de rest van het thema. Vanaf twee maten kan dat veld het
       niet meer dragen en zetten we hem uit: dan gaat het als een
       `items[]`-lijst mee, de vorm die /cart/add voor meerdere regels
       verwacht. Bij elke wijziging worden die regels opnieuw gezet, dus er
       blijft nooit een oude maat achter in het formulier. */
    syncSizes(idInput) {
      const chosen = this.sizeBtns.filter((b) => b.classList.contains('is-selected'));

      this.querySelectorAll('[data-klaasje-line]').forEach((el) => el.remove());
      idInput.disabled = chosen.length > 1;

      if (chosen.length === 1) {
        idInput.value = chosen[0].dataset.variantId;
      } else if (chosen.length > 1) {
        chosen.forEach((btn, i) => {
          this.buyForm.append(this.lineInput('items[' + i + '][id]', btn.dataset.variantId));
          this.buyForm.append(this.lineInput('items[' + i + '][quantity]', '1'));
        });
      }

      this.cartBtn.classList.toggle('is-ready', chosen.length > 0);
      if (chosen.length) this.cartBtn.removeAttribute('aria-disabled');
      else this.cartBtn.setAttribute('aria-disabled', 'true');
    }

    lineInput(name, value) {
      const el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      el.value = value;
      el.dataset.klaasjeLine = '';
      return el;
    }

    /* ── Binnenkomen in beeld ────────────────────────────────── */
    initReveal() {
      if (!('IntersectionObserver' in window)) {
        this.classList.add('is-visible');
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
      );
      io.observe(this);
    }

    /* ── Maat, prijs en voorraad achter een keuzelijst ────────── */
    /* Alleen de productpagina heeft die lijst nog; in het raster staan de
       maten los over de foto en stapt dit er meteen uit. */
    initVariants() {
      this.select = this.querySelector('[data-klaasje-card-select]');
      this.idInput = this.querySelector('[data-klaasje-card-id]');
      this.priceEl = this.querySelector('[data-klaasje-card-price]');
      this.stockEl = this.querySelector('[data-klaasje-card-stock]');
      this.addBtn = this.querySelector('[data-klaasje-card-add]');

      if (!this.select) return;
      this.select.addEventListener('change', () => this.onVariantChange());
      this.onVariantChange();
    }

    onVariantChange() {
      if (!this.select) return;
      const option = this.select.selectedOptions[0];
      if (!option || !option.value) {
        this.setStock('');
        return;
      }

      if (this.idInput) {
        this.idInput.value = option.value;
        this.idInput.disabled = false;
      }
      if (this.priceEl && option.dataset.price) this.priceEl.textContent = option.dataset.price;
      if (this.addBtn) {
        this.addBtn.disabled = option.disabled;
        this.addBtn.removeAttribute('aria-disabled');
        this.addBtn.classList.remove('loading');
      }

      this.setStock(this.stockLabel(option));
    }

    stockLabel(option) {
      // voorraad niet bijgehouden of doorverkoop zonder voorraad: niets weten,
      // dus niets zeggen
      if (option.dataset.managed !== '1') return '';
      const aantal = parseInt(option.dataset.stock, 10);
      if (isNaN(aantal) || aantal <= 0) return '';
      if (aantal <= EXACT_TOT) return `nog ${aantal} ${aantal === 1 ? 'stuk' : 'stuks'} beschikbaar`;
      return 'nog een aantal beschikbaar';
    }

    setStock(text) {
      if (!this.stockEl) return;
      this.stockEl.textContent = text;
      // alleen het exacte, lage aantal krijgt de urgentiekleur
      this.stockEl.classList.toggle('is-low', /^nog \d/.test(text));
    }
  }

  if (!customElements.get('klaasje-product-card')) {
    customElements.define('klaasje-product-card', KlaasjeProductCard);
  }
})();
